package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import com.siriusxm.pia.ApplicationContext
import com.siriusxm.pia.CapabilityAction
import com.siriusxm.pia.CapabilityQuery
import com.siriusxm.pia.can
import com.siriusxm.cmc.utils.arn
import unifiedcontent.aggregator.ConsumerTarget
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/** AVP action used by the catalog API to authorize creation of a producer. */
internal val ProducerCreateCapability =
    CapabilityAction("UnifiedContentAggregator_ProducerCreate")

/** AVP action used by the catalog API to authorize updates to a producer. */
internal val ProducerUpdateCapability =
    CapabilityAction("UnifiedContentAggregator_ProducerUpdate")

/** AVP action used by the catalog API to authorize creation of a consumer. */
internal val ConsumerCreateCapability =
    CapabilityAction("UnifiedContentAggregator_ConsumerCreate")

/** AVP action used by the catalog API to authorize updates to a consumer. */
internal val ConsumerUpdateCapability =
    CapabilityAction("UnifiedContentAggregator_ConsumerUpdate")

/**
 * AVP action the catalog API requires to add or remove a consumer target (UCA-1240). Policies scope
 * it by `consumerId` and `targetAccountId`; the policy alone decides which accounts are allowed.
 */
internal val ConsumerTargetEditCapability =
    CapabilityAction("UnifiedContentAggregator_ConsumerTargetEdit")

/**
 * The "may I add targets to this consumer" questions, one per configured AWS account, with the same
 * `consumerId` and `targetAccountId` the catalog API sends. Account-scoped policies only match when
 * the account is present, so a consumer-only question would always read as denied for a team grant.
 * The Add control shows when any of these is allowed. A consumer with no configured accounts gets one
 * question without `targetAccountId`, which only an unscoped (administrator) grant allows.
 */
internal fun consumerTargetAddProbes(consumerId: String, accountIds: Collection<String>): List<CapabilityQuery> {
    fun probe(account: String?) = CapabilityQuery(
        action = ConsumerTargetEditCapability,
        context = buildJsonObject {
            put("consumerId", consumerId)
            account?.let { put("targetAccountId", it) }
        }
    )
    val accounts = accountIds.filter { it.isNotBlank() }.distinct().sorted()
    return if (accounts.isEmpty()) listOf(probe(null)) else accounts.map { probe(it) }
}

/**
 * The "may I remove this target" question, built from the stored target the way the catalog API
 * builds it: exactly `consumerId` and `targetAccountId` (the only context fields the action
 * declares), with no account for an API Destination, which lives in a UCA account.
 */
internal fun consumerTargetRemoveProbe(consumerId: String, target: ConsumerTarget): CapabilityQuery =
    CapabilityQuery(
        action = ConsumerTargetEditCapability,
        context = buildJsonObject {
            put("consumerId", consumerId)
            if (target.apiDestination == null) {
                target.arn?.arn()?.accountId?.takeIf { it.isNotBlank() }?.let { put("targetAccountId", it) }
            }
        }
    )

/** Build the producer-update question with the same Cedar context as the catalog API. */
internal fun producerUpdateCapability(producerId: String): CapabilityQuery =
    CapabilityQuery(
        action = ProducerUpdateCapability,
        context = buildJsonObject { put("producerId", producerId) }
    )

/** Build the consumer-update question with the same Cedar context as the catalog API. */
internal fun consumerUpdateCapability(consumerId: String): CapabilityQuery =
    CapabilityQuery(
        action = ConsumerUpdateCapability,
        context = buildJsonObject { put("consumerId", consumerId) }
    )

/** State of a route-level capability check. */
private enum class CapabilityRouteState {
    CHECKING,
    ALLOWED,
    DENIED
}

/**
 * Resolve a route-level permission without briefly rendering the protected editor. A denied AVP
 * decision, a missing auth-check configuration, and a transient auth-check failure all fail closed
 * to [CapabilityRouteState.DENIED], matching [ApplicationContext.can].
 */
@Composable
private fun ApplicationContext.rememberCapabilityRouteState(
    query: CapabilityQuery
): State<CapabilityRouteState> {
    val state = remember(query) { mutableStateOf(CapabilityRouteState.CHECKING) }
    LaunchedEffect(query) {
        state.value = try {
            if (can(query)) CapabilityRouteState.ALLOWED else CapabilityRouteState.DENIED
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            console.error("AVP capability check failed while guarding a management route", e)
            CapabilityRouteState.DENIED
        }
    }
    return state
}

/**
 * Prevent a protected management editor from rendering until AVP has allowed its matching API
 * operation. This is a UI affordance only; the catalog API remains the authorization boundary.
 */
@Composable
internal fun ApplicationContext.capabilityRoute(
    query: CapabilityQuery,
    operation: String,
    content: @Composable () -> Unit
) {
    when (rememberCapabilityRouteState(query).value) {
        CapabilityRouteState.CHECKING -> Div { Text("Checking permission…") }
        CapabilityRouteState.ALLOWED -> content()
        CapabilityRouteState.DENIED -> Div {
            Text("You do not have permission to $operation.")
        }
    }
}

/** Action-only overload for create routes. */
@Composable
internal fun ApplicationContext.capabilityRoute(
    action: CapabilityAction,
    operation: String,
    content: @Composable () -> Unit
) = capabilityRoute(CapabilityQuery(action), operation, content)
